import React from 'react';
import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './redux/store';
import './i18n';
import { SnackbarProvider } from 'notistack';
import Loader from './components/loader';
import persistStore from 'redux-persist/es/persistStore';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';
import ErrorBoundaryWithTranslation from 'ErrorBoundaryWithTranslation';

// Datadog integration for performance monitoring and session replay
import { datadogRum, type PropagatorType } from '@datadog/browser-rum';

/**
 * All info will be changed after POC
 */
const apiUrl = process.env.REACT_APP_API_URL;
const RUM_PROPAGATORS: PropagatorType[] = ['tracecontext', 'datadog'];
const allowedTracingUrls = apiUrl
  ? [{ match: apiUrl, propagatorTypes: RUM_PROPAGATORS }]
  : [];

datadogRum.init({
    applicationId: '6d00d9c4-ce11-416f-997e-8de46d62464a',
    clientToken: 'pub5aebecb3f00c222f618ce79a885c99b5',
    site: 'datadoghq.com',
    service:'das-gateway-ui-v3.1',
    env: 'dev',
    
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    allowedTracingUrls,
    sessionSampleRate:  100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
});

// Datadog integration for performance monitoring and session replay

// import NoInternetConnection from "./components/no-internet/NoInternet";
let persistor = persistStore(store);
const App = lazy(
  () => import('./components/app/index' /* webpackChunkName: "App" */),
);

Sentry.init({
  dsn: `${process.env.REACT_APP_SENTRY_DSN}`,
  integrations: [
    new Sentry.BrowserTracing({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        `${process.env.REACT_DAS_APP_DEV_URL}`
      ],
    }),
    new Sentry.Replay({
      // Additional SDK configuration goes in here, for example:
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);
root.render(
  <React.Fragment>
   <SnackbarProvider
      maxSnack={10}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={3000}
      preventDuplicate
    >
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <Suspense fallback={<Loader isLoading={true} />}>
            <ErrorBoundaryWithTranslation>
              <App />
            </ErrorBoundaryWithTranslation>
          </Suspense>
        </PersistGate>
      </Provider>
    </SnackbarProvider>
  </React.Fragment>
);
// Performance monitoring
reportWebVitals();
